import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, useForm, useFormState } from 'react-final-form';
import { IconButton, Tooltip, useMediaQuery } from '@mui/material';
import { ThreeDots } from 'react-loader-spinner';
import passwordGenerator from '../../utils/passwordGeneration';

import CommonButton from '../../components/common-button/CommonButton';
import ChangePasswordInputWithLabel from './ChangePasswordInputWithLabel';
import localStyles from './ChangePassword.module.scss';
import Icon from '../../icons/Icon';
import { ICONS } from '../../icons';
import PasswordInput from '../../components/common-inputs/PasswordInput';
import CommonButtonLink from '../../components/common-button-link/CommonButtonLink';

import scss from '../Security.module.scss';

const ChangePasswordForm = ({ handleSubmit, isLoading = false }) => {
  const { t } = useTranslation('securityPage');
  const { valid, values } = useFormState();
  const form = useForm();
  const isTablet = useMediaQuery('(max-width: 1024px)');
  const [isGenerateOpen, setIsGenereateOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const isSubmitDisabled = !valid || isLoading;

  const handleGeneratePassword = useCallback(() => {
    if (!isGenerateOpen) {
      setIsGenereateOpen(true);
    }

    form.change('generatedPassword', passwordGenerator());
  }, [form, isGenerateOpen]);

  const handleCopyGeneratedPassword = useCallback(() => {
    if (isCopied) {
      return;
    }

    navigator.clipboard.writeText(values.generatedPassword);
    setIsCopied(true);

    setTimeout(() => {
      setIsCopied(false);
    }, 1000);
  }, [values, isCopied]);

  return (
    <form className={scss.form} onSubmit={handleSubmit}>
      <h1 className={scss.title}>{t('changePassword')}</h1>

      <div className={scss.items}>
        <ChangePasswordInputWithLabel
          name='newPassword'
          label={t('newPassword')}
          placeholder={isTablet ? t('password') : t('enterYourPassword')}
          disabled={isLoading}
          tabIndex={1}
        />

        <ChangePasswordInputWithLabel
          name='repeatPassword'
          label={t('confirmNewPassword')}
          placeholder={isTablet ? t('password') : t('enterYourPassword')}
          disabled={isLoading}
          tabIndex={2}
        />
      </div>

      <div className={localStyles.buttons}>
        <CommonButton styleColor='primary' type='submit' tabIndex={3} maxWidth={190} disabled={isSubmitDisabled}>
          {isLoading ? <ThreeDots height={15} width={40} /> : null}
          {t('updatePassword')}
        </CommonButton>

        <CommonButtonLink type='button' tabIndex={4} onClick={handleGeneratePassword}>
          {t('generatePassword')}
        </CommonButtonLink>
      </div>

      {isGenerateOpen ? (
        <div className={localStyles['password-generator']}>
          <Field name='generatedPassword'>
            {({ input }) => (
              <PasswordInput
                disabled={isLoading}
                tabIndex={5}
                formProps={input}
                LeftIcon={<Icon icon={ICONS.Lock} disableCommonStyles />}
              />
            )}
          </Field>

          <Tooltip title={isCopied ? 'Copied' : 'Copy'}>
            <IconButton className={localStyles['copy-icon-button']} onClick={handleCopyGeneratedPassword}>
              {isCopied ? (
                <Icon icon={ICONS.Tick} currentColor disableCommonColors />
              ) : (
                <Icon icon={ICONS.Copy} currentColor disableCommonColors />
              )}
            </IconButton>
          </Tooltip>
        </div>
      ) : null}
    </form>
  );
};

export default ChangePasswordForm;
